import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Switch, message, Button, Tabs, Form, Input, Select, InputNumber, Space } from "antd";
import { GetAllRankings } from "../../../../dao/jutge/GetAllRankings";
import { PostRankingIdProblemaId } from "../../../../dao/jutge/PostRankingIdProblemaId";
import { PlusOutlined, DeleteOutlined, EditOutlined, CloseCircleOutlined, CheckOutlined } from "@ant-design/icons";
import NuevoPremioModal from "./NuevoPremioModal";
import { GetProblemaRecompenses } from "../../../../dao/jutge/GetProblemaRecompenses";
import { PostProblemaRecompensa } from "../../../../dao/jutge/PostProblemaRecompensa";
import { PutProblemaRecompensa } from "../../../../dao/jutge/PutProblemaRecompensa";
import { DeleteProblemaRecompensa } from "../../../../dao/jutge/DeleteProblemaRecompensa";
import { GetJavamonConfig, GetSkins } from "../../../../dao/jutge/GetJavamonConfig";
import { GetShopItems } from "../../../../dao/jutge/GetShopItems";
import { GetUsuarisRecompensesProblema } from "../../../../dao/jutge/GetUsuarisRecompensesProblema";

const { TabPane } = Tabs;
const { Option } = Select;
const genericUrl = process.env.REACT_APP_API_URL;

interface Props {
    idProblema: string;
    idActivitat?: string;
    origenTipo?: string; // 'PROBLEMA' (por defecto) o 'PRACTICA'
    visible: boolean;
    onClose: (b: boolean) => void;
}

const PremiosModal: React.FC<Props> = ({ idProblema, idActivitat, origenTipo = 'PROBLEMA', visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [rankings, setRankings] = useState([]);
    const [recompenses, setRecompenses] = useState([]);
    const [usuarisPremiats, setUsuarisPremiats] = useState<any[]>([]);
    const [nuevoPremioVisible, setNuevoPremioVisible] = useState(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    
    // Configurations state
    const [javamons, setJavamons] = useState<any[]>([]);
    const [seasons, setSeasons] = useState<any[]>([]);
    const [shopItems, setShopItems] = useState<any[]>([]);
    const [skinsData, setSkinsData] = useState<any>({});
    const [tipoSeleccionado, setTipoSeleccionado] = useState<string>('');
    const [editingId, setEditingId] = useState<string | number | null>(null);

    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            handleCancelEdit();
            if (origenTipo === 'PROBLEMA') {
                loadRankings();
            }
            loadRecompenses();
            loadConfigData();
            loadUsuarisPremiats();
        }
    }, [visible]);

    async function loadUsuarisPremiats() {
        const data = await GetUsuarisRecompensesProblema(idProblema, idActivitat, origenTipo);
        setUsuarisPremiats(data || []);
    }

    async function loadConfigData() {
        const jConfig = await GetJavamonConfig();
        if (jConfig && jConfig.javamons) {
            setJavamons(typeof jConfig.javamons === 'string' ? JSON.parse(jConfig.javamons) : jConfig.javamons);
        } else {
            setJavamons([]);
        }
        if (jConfig && jConfig.seasons) {
            const parsed = typeof jConfig.seasons === 'string' ? (jConfig.seasons !== 'null' ? JSON.parse(jConfig.seasons) : []) : jConfig.seasons;
            setSeasons(Array.isArray(parsed) ? parsed : []);
        } else {
            setSeasons([]);
        }
        
        const sData = await GetSkins();
        if (sData) setSkinsData(sData);

        const shopData = await GetShopItems("0");
        if (shopData) setShopItems(shopData);
    }

    async function loadRankings() {
        setLoading(true);
        const data = await GetAllRankings(idProblema + "", origenTipo);
        const sortedData = (data || []).sort((a: any, b: any) => (b.punts || 0) - (a.punts || 0));
        setRankings(sortedData);
        setLoading(false);
    }

    async function loadRecompenses() {
        setLoading(true);
        const data = await GetProblemaRecompenses(idProblema + "", idActivitat, origenTipo);
        setRecompenses(data || []);
        setLoading(false);
    }

    async function toggleRanking(ranking: any, checked: boolean) {
        try {
            await PostRankingIdProblemaId(ranking.id, idProblema, origenTipo);
            message.success("Trofeu assignat");
            setHasChanges(true);
            await loadRankings();
        } catch {
            message.error("Error assignant el trofeu");
        }
    }

    function handleEditRecompensa(record: any) {
        setEditingId(record.id);
        setTipoSeleccionado(record.tipo);
        form.setFieldsValue({
            tipo: record.tipo,
            valor: record.valor,
            id_item: record.id_item ? (isNaN(Number(record.id_item)) ? record.id_item : Number(record.id_item)) : undefined,
            limit_quantity: record.limit_quantity,
            intentos_min: record.intentos_min || 1,
            intentos_max: record.intentos_max,
            time_limit: record.time_limit,
            nota_minima: record.nota_minima,
            grupo_seleccion: record.grupo_seleccion != null ? Number(record.grupo_seleccion) : 0
        });
    }

    function handleCancelEdit() {
        setEditingId(null);
        setTipoSeleccionado('');
        form.resetFields();
    }

    async function handleAddRecompensa(values: any) {
        setLoading(true);
        const payload = { ...values, origen_tipo: origenTipo };
        let success = false;
        if (editingId) {
            success = await PutProblemaRecompensa(idProblema, editingId, payload);
            if (success) {
                message.success("Recompensa actualitzada correctament");
                handleCancelEdit();
                setHasChanges(true);
                await loadRecompenses();
            } else {
                message.error("Error al actualitzar recompensa");
            }
        } else {
            success = await PostProblemaRecompensa(idProblema, payload);
            if (success) {
                message.success("Recompensa afegida correctament");
                handleCancelEdit();
                setHasChanges(true);
                await loadRecompenses();
            } else {
                message.error("Error al afegir recompensa");
            }
        }
        setLoading(false);
    }

    async function handleDeleteRecompensa(id: string) {
        setLoading(true);
        const success = await DeleteProblemaRecompensa(idProblema, id, origenTipo);
        if (success) {
            message.success("Recompensa eliminada");
            if (editingId === id) {
                handleCancelEdit();
            }
            setHasChanges(true);
            await loadRecompenses();
        } else {
            message.error("Error eliminant recompensa");
        }
        setLoading(false);
    }

    const columnsRanking = [
        { title: "ID", dataIndex: "id" },
        { title: "Nom", dataIndex: "nom" },
        { title: "Punts", dataIndex: "punts" },
        {
            title: "Color",
            dataIndex: "color",
            render: (c: string) => <Tag color={c}>{c}</Tag>
        },
        {
            title: "Afegir",
            render: (record: any) => (
                <Switch
                    checked={record.actiu === 1}
                    onChange={(checked) => toggleRanking(record, checked)}
                />
            )
        }
    ];

    const columnsRecompenses = [
        { title: "Tipus", dataIndex: "tipo", width: 110 },
        { title: "Valor", dataIndex: "valor", width: 80, align: 'center' as const, render: (v: any) => v ? <b style={{ color: '#faad14' }}>{v}</b> : '-' },
        { title: "Límit", dataIndex: "limit_quantity", width: 85, align: 'center' as const, render: (v: any) => v || 'Infinit' },
        { title: "Intents", width: 100, align: 'center' as const, render: (record: any) => `${record.intentos_min || 1} - ${record.intentos_max || '∞'}` },
        { title: "Temps", width: 90, align: 'center' as const, render: (record: any) => record.time_limit ? `${record.time_limit}m` : '∞' },
        { title: "Nota Mín", width: 95, align: 'center' as const, render: (record: any) => record.nota_minima ? `≥ ${record.nota_minima}%` : '-' },
        { 
            title: "Modalitat / Grup", 
            dataIndex: "grupo_seleccion",
            key: "grupo_seleccion",
            width: 190,
            render: (g: any) => {
                const grp = Number(g) || 0;
                if (grp > 0) {
                    return <Tag color="blue" style={{ borderRadius: 6, fontWeight: 500 }}>Selector Grup {grp} (Multiopció)</Tag>;
                }
                return <Tag color="green" style={{ borderRadius: 6 }}>Grup 0: Auto (Normal)</Tag>;
            }
        },
        { 
            title: "Item", 
            key: "item",
            ellipsis: true,
            render: (r: any) => {
                if (!r.id_item) return <span style={{ color: '#bbb' }}>-</span>;
                if (r.tipo === 'PUNTUACION' || r.tipo === 'JAVAMON_RANDOM') return <span style={{ color: '#bbb' }}>-</span>;
                if (r.tipo === 'JAVAMON_SEASON') {
                    const s = seasons.find((x: any) => x.id + "" === r.id_item + "");
                    return s ? `Temporada: ${s.nom}` : `Temporada #${r.id_item}`;
                }

                let list: any[] = [];
                if (r.tipo === "JAVAMON") list = javamons;
                else if (r.tipo === "SHOP_ITEM") list = shopItems;
                else if (r.tipo === "SKIN") list = skinsData.skins || [];
                else if (r.tipo === "ARMA") list = skinsData.armes || [];
                else if (r.tipo === "ESCUDO") list = skinsData.escuts || [];
                else if (r.tipo === "ARCO") list = skinsData.arcs || [];
                else if (r.tipo === "FLECHA") list = skinsData.flechas || [];
                else if (r.tipo === "MAGIA") list = skinsData.magias || [];
                else if (r.tipo === "AVATAR") list = skinsData.avatars || [];

                const item = list.find((x: any) => x.id + "" === r.id_item + "");
                
                let imgUrl = null;
                const urlBase = process.env.REACT_APP_API_URL;
                if (item) {
                    if (r.tipo === "JAVAMON") {
                        imgUrl = `${urlBase}:8061/jutge/game/image/javamon/${item.id}`;
                    } else if (r.tipo === "SHOP_ITEM") {
                        if (item.image && item.image.startsWith('data:')) imgUrl = item.image;
                        else if (item.image && !item.image.includes('/')) imgUrl = `data:image/png;base64,${item.image}`;
                        else imgUrl = `${urlBase}:8061/jutge/game/image/shop/${item.id}`;
                    } else {
                        if (item.has_image || item.imagen) {
                            if (item.imagen && item.imagen.startsWith('data:')) imgUrl = item.imagen;
                            else if (item.imagen) imgUrl = `data:image/png;base64,${item.imagen}`;
                            else {
                                let typeSegment = r.tipo.toLowerCase();
                                if (typeSegment === "escudo") typeSegment = "escut";
                                if (typeSegment === "arco") typeSegment = "arc";
                                if (typeSegment === "shop_item") typeSegment = "shop";
                                imgUrl = `${urlBase}:8061/jutge/game/image/${typeSegment}/${item.id}`;
                            }
                        }
                    }
                } else {
                    let typeSegment = r.tipo.toLowerCase();
                    if (typeSegment === "escudo") typeSegment = "escut";
                    if (typeSegment === "arco") typeSegment = "arc";
                    if (typeSegment === "shop_item") typeSegment = "shop";
                    if (typeSegment === "javamon") {
                        imgUrl = `${urlBase}:8061/jutge/game/image/javamon/${r.id_item}`;
                    } else {
                        imgUrl = `${urlBase}:8061/jutge/game/image/${typeSegment}/${r.id_item}`;
                    }
                }

                const itemName = item ? (item.nom || item.name) : `ID: ${r.id_item}`;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {imgUrl && <img src={imgUrl} alt="img" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={itemName}>{itemName}</span>
                    </div>
                );
            }
        },
        {
            title: "Accions",
            key: "accions",
            width: 100,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (record: any) => (
                <Space size="small">
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        style={{ color: editingId === record.id ? '#1890ff' : '#595959' }}
                        onClick={() => handleEditRecompensa(record)} 
                        title="Editar"
                    />
                    <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => handleDeleteRecompensa(record.id)} 
                        title="Eliminar"
                    />
                </Space>
            )
        }
    ];

    return (
        <>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18, fontWeight: 600 }}>Configurar Problema</span>
                    </div>
                }
                open={visible}
                onCancel={() => onClose(hasChanges)}
                onOk={() => onClose(hasChanges)}
                width={1080}
                style={{ top: 25 }}
                footer={null}
            >
                <Tabs defaultActiveKey="2">
                    {(origenTipo === 'PROBLEMA' || origenTipo === 'BBDD') && (
                    <TabPane tab="Trofeus" key="1">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ marginBottom: 15 }}
                            onClick={() => setNuevoPremioVisible(true)}
                        >
                            Nou trofeu
                        </Button>

                        <Table
                            rowKey="id"
                            dataSource={rankings}
                            columns={columnsRanking}
                            loading={loading}
                            pagination={false}
                        />
                    </TabPane>
                    
                    )}
                    <TabPane tab="Recompenses" key="2">
                        {editingId && (
                            <div style={{ 
                                marginBottom: 14, 
                                padding: '10px 16px', 
                                background: '#f0f7ff', 
                                border: '1px solid #adc6ff', 
                                borderRadius: 8, 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                boxShadow: '0 2px 6px rgba(24,144,255,0.08)'
                            }}>
                                <span style={{ color: '#0958d9', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    ✏️ <b>Modificant recompensa #{editingId}</b> — Fes els canvis i prem "Guardar canvis".
                                </span>
                                <Button size="small" type="link" onClick={handleCancelEdit} style={{ color: '#ff4d4f' }}>
                                    Cancel·lar edició
                                </Button>
                            </div>
                        )}
                        <div style={{ 
                            background: '#fafafa', 
                            border: '1px solid #f0f0f0', 
                            borderRadius: 8, 
                            padding: '14px 16px 6px 16px', 
                            marginBottom: 16 
                        }}>
                            <Form form={form} layout="inline" onFinish={handleAddRecompensa}>
                            <Form.Item name="tipo" rules={[{ required: true, message: 'Tipus necessari' }]}>
                                <Select placeholder="Tipus" style={{ width: 170 }} onChange={(val) => setTipoSeleccionado(val)}>
                                    <Option value="PUNTUACION">Puntuació</Option>
                                    <Option value="JAVAMON_RANDOM">Javamon (Random)</Option>
                                    <Option value="JAVAMON">Javamon Específic</Option>
                                    <Option value="JAVAMON_SEASON">Javamon de Temporada</Option>
                                    <Option value="SHOP_ITEM">Objecte de Tienda</Option>
                                    <Option value="SKIN">Skin</Option>
                                    <Option value="ARMA">Arma</Option>
                                    <Option value="ESCUDO">Escut</Option>
                                    <Option value="ARCO">Arc</Option>
                                    <Option value="FLECHA">Fletxa</Option>
                                    <Option value="MAGIA">Màgia</Option>
                                    <Option value="AVATAR">Avatar</Option>
                                </Select>
                            </Form.Item>
                            
                            <Form.Item name="valor" tooltip="Quantitat de punts (si és puntuació)">
                                <InputNumber placeholder="Valor" disabled={tipoSeleccionado !== 'PUNTUACION'} />
                            </Form.Item>

                            {tipoSeleccionado === "JAVAMON_SEASON" ? (
                                <Form.Item name="id_item" rules={[{ required: true, message: 'Temporada necessària' }]} tooltip="Temporada de la que sortirà el javamon">
                                    <Select
                                        placeholder="Selecciona Temporada"
                                        style={{ width: 220 }}
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {seasons.map((s: any) => (
                                            <Option key={s.id} value={s.id} label={s.nom}>{s.nom}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : tipoSeleccionado && tipoSeleccionado !== "PUNTUACION" && tipoSeleccionado !== "JAVAMON_RANDOM" ? (
                                <Form.Item name="id_item" rules={[{ required: true, message: 'ID necessari' }]} tooltip="Selecciona l'ítem">
                                    <Select 
                                        placeholder="Selecciona Item" 
                                        style={{ width: 250 }} 
                                        showSearch 
                                        optionFilterProp="children"
                                    >
                                        {(() => {
                                            let list: any[] = [];
                                            if (tipoSeleccionado === "JAVAMON") list = javamons;
                                            else if (tipoSeleccionado === "SHOP_ITEM") list = shopItems;
                                            else if (tipoSeleccionado === "SKIN") list = skinsData.skins || [];
                                            else if (tipoSeleccionado === "ARMA") list = skinsData.armes || [];
                                            else if (tipoSeleccionado === "ESCUDO") list = skinsData.escuts || [];
                                            else if (tipoSeleccionado === "ARCO") list = skinsData.arcs || [];
                                            else if (tipoSeleccionado === "FLECHA") list = skinsData.flechas || [];
                                            else if (tipoSeleccionado === "MAGIA") list = skinsData.magias || [];
                                            else if (tipoSeleccionado === "AVATAR") list = skinsData.avatars || [];

                                            return list.map(item => {
                                                let imgUrl = null;
                                                if (tipoSeleccionado === "JAVAMON") {
                                                    imgUrl = `${genericUrl}:8061/jutge/game/image/javamon/${item.id}`;
                                                } else if (tipoSeleccionado === "SHOP_ITEM") {
                                                    // Igual que en la tabla: item.image solo si es base64 real;
                                                    // si es ruta o viene vacio, usamos el endpoint de imagen.
                                                    if (item.image && item.image.startsWith('data:')) imgUrl = item.image;
                                                    else if (item.image && !item.image.includes('/')) imgUrl = `data:image/png;base64,${item.image}`;
                                                    else imgUrl = `${genericUrl}:8061/jutge/game/image/shop/${item.id}`;
                                                } else {
                                                    if (item.has_image || item.imagen) {
                                                        if (item.imagen && item.imagen.startsWith('data:')) imgUrl = item.imagen;
                                                        else if (item.imagen) imgUrl = `data:image/png;base64,${item.imagen}`;
                                                        else {
                                                            let typeSegment = tipoSeleccionado.toLowerCase();
                                                            if (typeSegment === "escudo") typeSegment = "escut";
                                                            if (typeSegment === "arco") typeSegment = "arc";
                                                            if (typeSegment === "shop_item") typeSegment = "shop";
                                                            imgUrl = `${genericUrl}:8061/jutge/game/image/${typeSegment}/${item.id}`;
                                                        }
                                                    }
                                                }
                                                return (
                                                    <Option key={item.id} value={item.id} label={item.nom || item.name}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            {imgUrl && <img src={imgUrl} alt="img" style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />}
                                                            {item.nom || item.name || `Item ${item.id}`}
                                                        </div>
                                                    </Option>
                                                );
                                            });
                                        })()}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <Form.Item name="id_item" tooltip="ID del Javamon o Item (si aplica)">
                                    <InputNumber placeholder="ID Item" disabled={tipoSeleccionado === "JAVAMON_RANDOM" || tipoSeleccionado === "PUNTUACION"} />
                                </Form.Item>
                            )}
                            
                            <Form.Item name="limit_quantity" tooltip="Quantitat màxima (deixa en blanc per infinit)">
                                <InputNumber placeholder="Límit Unitats" />
                            </Form.Item>

                            <Form.Item name="intentos_min" initialValue={1} tooltip="Mínim d'intents per aconseguir-ho">
                                <InputNumber placeholder="Intents Mín" min={1} />
                            </Form.Item>

                            <Form.Item name="intentos_max" tooltip="Màxim d'intents (buit = infinit)">
                                <InputNumber placeholder="Intents Màx" min={1} />
                            </Form.Item>

                            <Form.Item name="time_limit" tooltip="Temps màxim en minuts (buit = infinit)">
                                <InputNumber placeholder="Temps Límit (min)" min={1} />
                            </Form.Item>

                            <Form.Item name="nota_minima" tooltip="Nota mínima necessària en % (ex: 70)">
                                <InputNumber placeholder="% Nota Mín" min={0} max={100} />
                            </Form.Item>

                            <Form.Item 
                                name="grupo_seleccion" 
                                initialValue={0} 
                                tooltip="0 = Automàtica (s'obté directament com a recompensa normal). 1, 2, ... = Multiopció (l'alumne n'ha de triar NOMÉS UNA dins del mateix grup de selecció)"
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Tag color="cyan" style={{ margin: 0, fontWeight: 500 }}>
                                        Selector Grup:
                                    </Tag>
                                    <Form.Item name="grupo_seleccion" noStyle initialValue={0}>
                                        <InputNumber 
                                            placeholder="Grup (0 = Auto)" 
                                            min={0} 
                                            style={{ width: 140 }} 
                                            addonAfter={<span style={{ fontSize: 11, color: '#888' }}>(0=Auto)</span>}
                                        />
                                    </Form.Item>
                                </div>
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        icon={editingId ? <CheckOutlined /> : <PlusOutlined />} 
                                        loading={loading}
                                    >
                                        {editingId ? "Guardar" : "Afegir"}
                                    </Button>
                                    {editingId && (
                                        <Button 
                                            icon={<CloseCircleOutlined />} 
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel·lar
                                        </Button>
                                    )}
                                </Space>
                            </Form.Item>
                        </Form>
                        </div>

                        <Table
                            rowKey="id"
                            dataSource={recompenses}
                            columns={columnsRecompenses}
                            loading={loading}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 1000 }}
                            rowClassName={(record: any) => record.id === editingId ? 'ant-table-row-selected' : ''}
                        />
                    </TabPane>

                    <TabPane tab="Historial de Recompenses" key="3">
                        <Table
                            rowKey="id"
                            dataSource={usuarisPremiats}
                            pagination={false}
                            columns={[
                                {
                                    title: "Avatar",
                                    dataIndex: "avatar",
                                    key: "avatar",
                                    render: (avatar) => avatar ? <img src={avatar} alt="avatar" style={{width: 30, height: 30, borderRadius: '50%'}} /> : '-'
                                },
                                {
                                    title: "Nom",
                                    dataIndex: "nom",
                                    key: "nom"
                                },
                                {
                                    title: "Cognoms",
                                    dataIndex: "cognoms",
                                    key: "cognoms"
                                },
                                {
                                    title: "Recompenses",
                                    dataIndex: "rewards_json",
                                    key: "rewards_json",
                                    render: (rewardsJson, row: any) => {
                                        let items = [];
                                        if (row.id_javamon) {
                                            const urlBase = process.env.REACT_APP_API_URL;
                                            let imgUrl = `${urlBase}:8061/jutge/game/image/javamon/${row.id_javamon}`;
                                            const isEscapado = row.estado_javamon === 'ESCAPADO';
                                            const ballInfo = row.javaball_lanzada
                                                ? ` | Javaball: ${row.javaball_lanzada}${row.javaball_origen ? ` (${row.javaball_origen === 'GAME' ? 'Juego' : 'Web'})` : ''}`
                                                : (isEscapado ? ' | Sin lanzar Javaball' : '');
                                            items.push(
                                                <div key="javamon" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                                        <img
                                                            src={imgUrl}
                                                            title={`${isEscapado ? 'Javamon (Escapado)' : 'Javamon (Capturado)'}${ballInfo}`}
                                                            alt="javamon"
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                objectFit: 'contain',
                                                                filter: isEscapado ? 'grayscale(100%) opacity(50%)' : 'none'
                                                            }}
                                                        />
                                                        {isEscapado && (
                                                            <span style={{
                                                                position: 'absolute',
                                                                top: -5,
                                                                right: -5,
                                                                color: 'red',
                                                                fontSize: '16px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                ✕
                                                            </span>
                                                        )}
                                                    </div>
                                                    {row.javaball_lanzada ? (
                                                        <Tag color={isEscapado ? "volcano" : "geekblue"} style={{ margin: 0, fontSize: 11 }}>
                                                            🎯 {row.javaball_lanzada}
                                                        </Tag>
                                                    ) : (
                                                        isEscapado && (
                                                            <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                                                                Sin Javaball
                                                            </Tag>
                                                        )
                                                    )}
                                                </div>
                                            );
                                        }

                                        if (rewardsJson) {
                                            try {
                                                const rewards = typeof rewardsJson === 'string' ? JSON.parse(rewardsJson) : rewardsJson;
                                                if (rewards && rewards.length > 0) {
                                                    rewards.forEach((r: any, idx: number) => {
                                                        if (r.tipo === 'PUNTUACION') {
                                                            items.push(<Tag key={`r_${idx}`} color="gold">+{r.valor} Punts</Tag>);
                                                        } else {
                                                            const urlBase = process.env.REACT_APP_API_URL;
                                                            let typeSegment = r.tipo.toLowerCase();
                                                            if (typeSegment === "escudo") typeSegment = "escut";
                                                            if (typeSegment === "arco") typeSegment = "arc";
                                                            if (typeSegment === "shop_item") typeSegment = "shop";
                                                            let imgUrl = `${urlBase}:8061/jutge/game/image/${typeSegment}/${r.id_item}`;
                                                            // We skip rendering the generic "JAVAMON_RANDOM" or "JAVAMON" item here
                                                            // because we already rendered the specific one from row.id_javamon.
                                                            if (r.tipo !== 'JAVAMON_RANDOM' && r.tipo !== 'JAVAMON') {
                                                                items.push(<img key={`img_${idx}`} src={imgUrl} title={r.tipo} alt={r.tipo} style={{width: 24, height: 24, objectFit: 'contain'}} />);
                                                            }
                                                        }
                                                    });
                                                }
                                            } catch (e) {
                                                // ignore json parse error
                                            }
                                        }
                                        
                                        if (items.length === 0) return '-';
                                        
                                        return <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>{items}</div>;
                                    }
                                }
                            ]}
                        />
                    </TabPane>
                </Tabs>
            </Modal>

            {/* Modal de creación de nuevo premio */}
            <NuevoPremioModal
                visible={nuevoPremioVisible}
                origenTipo={origenTipo}
                onClose={async (created: any) => {
                    setNuevoPremioVisible(false);
                    if (created) await loadRankings();
                }}
            />
        </>
    );
};

export default PremiosModal;
